import React from 'react';
import { useTranslate } from '../../../mixins/translate/translate';
import { openApiFetchDelete, openApiFetchGet, openApiFetchPost } from '../../../store/openApiFetch';
import { IconEditButton } from '../../common/iconButtons/IconEditButton';
import { IconRemoveButton } from '../../common/iconButtons/IconRemoveButton';
import {
    EditPanelButton,
    EditPanelInput,
    EditPanelSelect,
    EditPanelSwitch,
    PanelBody,
    Table,
    TableBody,
    TableButtons,
    TableData,
    TableHead,
    TableHeader,
    TableHeadRow,
    TableRow,
} from '../../elements';
import { NullableBoolean } from '../../utils/NullableBoolean';
import { NullableFormattedNumber } from '../../utils/NullableFormattedNumber';
import { useReactAsync } from '../../utils/useReactAsync';
import { PotreeConvert } from '../Worker/PotreeConvert';

async function loadItems({ surveyId }: { surveyId: string }) {
    const result = await openApiFetchGet('/api/surveys/{surveyId}/pointClouds', { surveyId });
    return result;
}

interface ITemperatureProps {
    isShowEditButton: boolean;
    pointCloudId: string;
    startTemperatureRange: number | null;
    endTemperatureRange: number | null;
    onChange: (value: { startTemperatureRange: number | null; endTemperatureRange: number | null }) => void;
}

function Temperature(props: ITemperatureProps) {
    const translate = useTranslate();

    const [isEditing, setIsEditing] = React.useState(false);

    const [startTemperatureRange, setStartTemperatureRange] = React.useState(props.startTemperatureRange);
    const [endTemperatureRange, setEndTemperatureRange] = React.useState(props.endTemperatureRange);

    function save() {
        props.onChange({ startTemperatureRange, endTemperatureRange });
        setIsEditing(false);
    }

    if (!isEditing) {
        return (
            <>
                <NullableFormattedNumber value={props.startTemperatureRange} />
                &nbsp;-&nbsp;
                <NullableFormattedNumber value={props.endTemperatureRange} />
                {props.isShowEditButton && (
                    <EditPanelButton onClick={() => setIsEditing(true)} small={true} text={translate('common.edit')} primary />
                )}
            </>
        );
    }

    return (
        <td>
            <td>
                <EditPanelInput
                    autoFocus
                    maxlength={3}
                    name={`${props.pointCloudId}startTemperatureRange`}
                    handleChange={(value) => setStartTemperatureRange(value === '' ? null : parseFloat(value))}
                    value={startTemperatureRange}
                    restrictToNumbers
                />
            </td>
            <td>
                <EditPanelInput
                    name={`${props.pointCloudId}_endTemperatureRange`}
                    handleChange={(value) => setEndTemperatureRange(value === '' ? null : parseFloat(value))}
                    value={endTemperatureRange}
                    restrictToNumbers
                />
            </td>
            <td>
                <EditPanelButton small={true} text={translate('common.save')} onClick={save} primary />
            </td>
        </td>
    );
}

interface ISurveyEditPointCloudsProps {
    surveyId: string;
    objectId: string;
    onUpdated: () => void;
}
export function SurveyEditPointClouds(props: ISurveyEditPointCloudsProps) {
    const translate = useTranslate();
    const [newFileName, setNewFileName] = React.useState<string>();
    const [editItemId, setEditItemId] = React.useState<string>();

    const { data, reload, isLoading } = useReactAsync(loadItems, { surveyId: props.surveyId }, []);

    async function remove(id: string) {
        await openApiFetchDelete('/api/surveys/{surveyId}/pointClouds/{surveyPointCloudId}', { surveyId: props.surveyId, surveyPointCloudId: id });
        await reload();
        props.onUpdated();
    }

    async function update(
        id: string,
        item: {
            isVisible?: undefined | boolean;
            display?: undefined | string;
            filename?: undefined | string;
            startTemperatureRange?: undefined | number | null;
            endTemperatureRange?: undefined | number | null;
        },
    ) {
        await openApiFetchPost(
            '/api/surveys/{surveyId}/pointClouds/{surveyPointCloudId}',
            { surveyId: props.surveyId, surveyPointCloudId: id },
            item,
        );
        await reload();
        if (!item.filename) {
            props.onUpdated();
        }
        setEditItemId(undefined);
    }

    async function onConverted() {
        await reload();
        props.onUpdated();
    }

    if (isLoading) {
        return null;
    }

    return (
        <>
            <PanelBody isForm={true}>
                <PotreeConvert surveyId={props.surveyId} objectId={props.objectId} onConverted={onConverted} />
                <Table>
                    <TableHead>
                        <TableHeadRow>
                            <TableHeader>{translate('common.filename')}</TableHeader>
                            <TableHeader>{translate('survey.display')}</TableHeader>
                            <TableHeader>{translate('survey.visible')}</TableHeader>
                            <TableHeader>{translate('survey.temperature')}</TableHeader>
                            <TableHeader>&nbsp;</TableHeader>
                        </TableHeadRow>
                    </TableHead>
                    <TableBody>
                        {data?.map((item) => (
                            <TableRow key={item.id}>
                                <TableData>
                                    {(editItemId === item.id && (
                                        <input
                                            type="text"
                                            name="namePointCloud"
                                            id="namePointCloud"
                                            className="gui-input fs13"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                            }}
                                            defaultValue={item.filename ?? ''}
                                            value={newFileName}
                                            onBlur={async () => await update(item.id, { filename: newFileName })}
                                            onChange={(e) => setNewFileName(e.target.value)}
                                            onKeyDown={async (e) => {
                                                if (e.key === 'Enter') {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    await update(item.id, { filename: newFileName });
                                                }
                                            }}
                                        />
                                    )) ||
                                        item.filename}
                                </TableData>
                                <TableData>
                                    {(editItemId === item.id && (
                                        <EditPanelSelect
                                            name={`${item.id}_material`}
                                            value={item.display}
                                            handleChange={async (value) => update(item.id, { display: value })}
                                            options={[
                                                { value: 'Intensity', text: 'Intensity' },
                                                { value: 'Elevation', text: 'Elevation' },
                                                { value: 'RGB', text: 'RGB' },
                                            ]}
                                        />
                                    )) ||
                                        item.display}
                                </TableData>
                                <TableData>
                                    {(editItemId === item.id && (
                                        <EditPanelSwitch
                                            name={`${item.id}_isVisible`}
                                            handleChange={async () => update(item.id, { isVisible: !item.isVisible })}
                                            value={item.isVisible}
                                        />
                                    )) || (
                                        <>
                                            <NullableBoolean value={item.isVisible} useSymbols />
                                        </>
                                    )}
                                </TableData>

                                <TableData>
                                    <Temperature
                                        pointCloudId={item.id}
                                        startTemperatureRange={item.startTemperatureRange}
                                        isShowEditButton={editItemId === item.id}
                                        endTemperatureRange={item.endTemperatureRange}
                                        onChange={async (value) => update(item.id, value)}
                                    />
                                </TableData>
                                <TableButtons>
                                    <IconEditButton onClick={() => setEditItemId(item.id)} />
                                    <IconRemoveButton
                                        removeQuestion={translate('common.confirmDelete', item.filename ?? '')}
                                        onClick={async () => remove(item.id)}
                                    />
                                </TableButtons>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </PanelBody>
        </>
    );
}
